import { createSlice, type PayloadAction } from "@reduxjs/toolkit"
import { coreSlice } from "./core-slice"

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyValue = any

export interface OptimizationState {
  isOptimizePanelExpanded: boolean
  optimizeConfiguration: AnyValue
  previewOptimizeConfiguration: AnyValue
  optimizationExperimentSource: AnyValue
  optimizationSourceKind: string
  lastAppliedOptimizationSourceKey: AnyValue
  hasUserAppliedOptimization: boolean
  isPreferencesLoaded: boolean
}

export type OptimizationRootState = Partial<Record<string, OptimizationState>>

export const DEFAULT_OPTIMIZATION_STATE: OptimizationState = {
  isOptimizePanelExpanded: false,
  optimizeConfiguration: null,
  previewOptimizeConfiguration: null,
  optimizationExperimentSource: null,
  optimizationSourceKind: "original",
  lastAppliedOptimizationSourceKey: null,
  hasUserAppliedOptimization: false,
  isPreferencesLoaded: false,
}

// Applied when backend sends preferences via setPreferences.
// isOptimizePanelExpanded / optimizeConfiguration / previewOptimizeConfiguration are
// actively two-way synced; optimizationExperimentSource / optimizationSourceKind are
// applied from backend but not actively written back by the sync hook.
const OPTIMIZATION_PREFERENCE_FIELDS = new Set([
  "isOptimizePanelExpanded",
  "optimizeConfiguration",
  "previewOptimizeConfiguration",
  "optimizationExperimentSource",
  "optimizationSourceKind",
])

function ensureInstance(state: OptimizationRootState, dashboardId: string): OptimizationState {
  if (!state[dashboardId]) {
    state[dashboardId] = { ...DEFAULT_OPTIMIZATION_STATE }
  }
  return state[dashboardId] as OptimizationState
}

export const optimizationSlice = createSlice({
  name: "dashboard/optimization",
  initialState: {} as OptimizationRootState,
  reducers: {
    setPreferences(
      state,
      action: PayloadAction<{ dashboardId: string } & Record<string, AnyValue>>,
    ) {
      const { dashboardId, ...prefs } = action.payload
      const instance = ensureInstance(state, dashboardId)
      for (const key of Object.keys(prefs)) {
        if (OPTIMIZATION_PREFERENCE_FIELDS.has(key)) {
          (instance as Record<string, AnyValue>)[key] = prefs[key]
        }
      }
      instance.isPreferencesLoaded = true
    },

    setIsOptimizePanelExpanded(
      state,
      action: PayloadAction<{ dashboardId: string, isOptimizePanelExpanded: boolean }>,
    ) {
      const { dashboardId, isOptimizePanelExpanded } = action.payload
      const instance = ensureInstance(state, dashboardId)
      instance.isOptimizePanelExpanded = isOptimizePanelExpanded
    },

    setOptimizeConfiguration(
      state,
      action: PayloadAction<{ dashboardId: string, optimizeConfiguration: AnyValue }>,
    ) {
      const { dashboardId, optimizeConfiguration } = action.payload
      const instance = ensureInstance(state, dashboardId)
      instance.optimizeConfiguration = optimizeConfiguration
    },

    setOptimizeConfigurationFromUser(
      state,
      action: PayloadAction<{ dashboardId: string, optimizeConfiguration: AnyValue }>,
    ) {
      const { dashboardId, optimizeConfiguration } = action.payload
      const instance = ensureInstance(state, dashboardId)
      instance.optimizeConfiguration = optimizeConfiguration
      instance.hasUserAppliedOptimization = true
    },

    setPreviewOptimizeConfiguration(
      state,
      action: PayloadAction<{ dashboardId: string, previewOptimizeConfiguration: AnyValue }>,
    ) {
      const { dashboardId, previewOptimizeConfiguration } = action.payload
      const instance = ensureInstance(state, dashboardId)
      instance.previewOptimizeConfiguration = previewOptimizeConfiguration
    },

    setOptimizationExperimentSource(
      state,
      action: PayloadAction<{ dashboardId: string, optimizationExperimentSource: AnyValue }>,
    ) {
      const { dashboardId, optimizationExperimentSource } = action.payload
      const instance = ensureInstance(state, dashboardId)
      instance.optimizationExperimentSource = optimizationExperimentSource
    },

    setOptimizationSourceKind(
      state,
      action: PayloadAction<{ dashboardId: string, optimizationSourceKind: string }>,
    ) {
      const { dashboardId, optimizationSourceKind } = action.payload
      const instance = ensureInstance(state, dashboardId)
      instance.optimizationSourceKind = optimizationSourceKind
      if (optimizationSourceKind === "original") {
        instance.optimizationExperimentSource = null
      }
    },

    setLastAppliedOptimizationSourceKey(
      state,
      action: PayloadAction<{ dashboardId: string, lastAppliedOptimizationSourceKey: AnyValue }>,
    ) {
      const { dashboardId, lastAppliedOptimizationSourceKey } = action.payload
      const instance = ensureInstance(state, dashboardId)
      instance.lastAppliedOptimizationSourceKey = lastAppliedOptimizationSourceKey
    },
  },

  extraReducers(builder) {
    builder
      .addCase(coreSlice.actions.setInstrument, (state, action) => {
        const { dashboardId } = action.payload
        const instance = ensureInstance(state, dashboardId)
        instance.hasUserAppliedOptimization = false
        instance.optimizationExperimentSource = null
        instance.optimizationSourceKind = "original"
        instance.lastAppliedOptimizationSourceKey = null
      })
      .addCase(coreSlice.actions.setComparisonInstrument, (state, action) => {
        const { dashboardId, instrument } = action.payload
        if (!instrument) {
          const instance = ensureInstance(state, dashboardId)
          instance.hasUserAppliedOptimization = false
          instance.lastAppliedOptimizationSourceKey = null
        }
      })
  },
})

export default optimizationSlice.reducer
